import { useState } from 'react'
import Modal from './Modal'
import { api, ApiClientError } from '@/lib/api'
import { toastError, toastSuccess } from '@/store/toast'
import type { Patient } from '@yui-receipt/shared'

type Props = {
  open: boolean
  onClose: () => void
  patient: Pick<Patient, 'id' | 'hn' | 'nameTh' | 'consentAudio' | 'consentAiScribe'>
  onGranted: (patient: Patient) => void
}

export default function ConsentModal({ open, onClose, patient, onGranted }: Props) {
  const [consentAudio, setConsentAudio] = useState(patient.consentAudio)
  const [consentAiScribe, setConsentAiScribe] = useState(patient.consentAiScribe)
  const [saving, setSaving] = useState(false)

  const bothGranted = consentAudio && consentAiScribe

  async function handleSave() {
    setSaving(true)
    try {
      const res = await api<{ patient: Patient }>(`/patients/${patient.id}/consents`, {
        body: { consentAudio, consentAiScribe },
      })
      toastSuccess('บันทึกความยินยอมสำเร็จ')
      onGranted(res.patient)
      onClose()
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'บันทึกไม่สำเร็จ'
      toastError(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="ความยินยอม PDPA">
      <div className="space-y-4">
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          คนไข้ <strong>{patient.nameTh}</strong> (HN {patient.hn}) ยังไม่ได้ให้ความยินยอม
          ตาม PDPA — กรุณาขอความยินยอมจากคนไข้ก่อนเริ่มอัดเสียง
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-brand-600"
            checked={consentAudio}
            onChange={(e) => setConsentAudio(e.target.checked)}
          />
          <div>
            <div className="font-medium text-sm">ยินยอมให้บันทึกเสียง</div>
            <div className="text-xs text-slate-500">
              อนุญาตให้บันทึกเสียงระหว่างการตรวจ (เก็บ 90 วัน)
            </div>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 accent-brand-600"
            checked={consentAiScribe}
            onChange={(e) => setConsentAiScribe(e.target.checked)}
          />
          <div>
            <div className="font-medium text-sm">ยินยอมให้ AI สร้างบันทึกการรักษา</div>
            <div className="text-xs text-slate-500">
              อนุญาตให้ระบบถอดเสียงและสร้าง SOAP note อัตโนมัติ
            </div>
          </div>
        </label>

        <div className="flex gap-2 pt-2">
          <button
            className="btn-primary flex-1"
            onClick={handleSave}
            disabled={saving || !bothGranted}
          >
            {saving ? 'กำลังบันทึก...' : 'บันทึกความยินยอม'}
          </button>
          <button className="btn-ghost flex-1" onClick={onClose}>
            ยกเลิก
          </button>
        </div>
      </div>
    </Modal>
  )
}
